import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { studentApi } from '@/api';
import {
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  ErrorState,
  Icon,
  Input,
  Skeleton,
  useToast,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';

const QUERY_KEY = ['portfolio-sharing'];

/**
 * The backend returns a client path, and the absolute link is built from this
 * origin -- the server's FRONTEND_URL is not reliable on every deployment
 * (see InviteCard).
 */
function absoluteUrl(path) {
  if (!path || typeof window === 'undefined') return path ?? '';
  return new URL(path, window.location.origin).toString();
}

function Switch({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition',
        'focus-visible:outline-brand-600 focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-60',
        checked ? 'bg-brand-600' : 'bg-ink-muted/35',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'size-4 rounded-full bg-white shadow-sm transition',
          checked ? 'translate-x-4.5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

/**
 * "Shareable portfolio": turn on a public, link-only page of the profile.
 * Off by default; turning it off or getting a new link kills the old one.
 */
export function PortfolioShareCard() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: studentApi.getPortfolioSharing,
  });

  function keep(sharing) {
    queryClient.setQueryData(QUERY_KEY, sharing);
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }

  const toggle = useMutation({
    mutationFn: (enabled) => studentApi.setPortfolioSharing(enabled),
    onSuccess: (sharing) => {
      keep(sharing);
      toast.success(sharing.enabled ? 'Your portfolio link is live' : 'Portfolio link turned off');
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not change portfolio sharing')),
  });

  const regenerate = useMutation({
    mutationFn: () => studentApi.regeneratePortfolioLink(),
    onSuccess: (sharing) => {
      keep(sharing);
      setConfirming(false);
      toast.success('New link created. The old one no longer works.');
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not create a new link')),
  });

  const url = absoluteUrl(data?.path);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Portfolio link copied');
    } catch {
      toast.error('Could not copy. Select the link and copy it yourself.');
    }
  }

  let body;
  if (isPending) {
    body = <Skeleton className="h-8 w-full" />;
  } else if (isError) {
    body = <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  } else {
    body = (
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-ink text-sm font-medium">
              {data.enabled ? 'Sharing is on' : 'Sharing is off'}
            </p>
            <p className="text-ink-muted mt-0.5 text-xs">
              Anyone with the link sees your photo, headline, studies, experience, skills, projects,
              certifications and links, and can download your primary CV. Never your email, phone or
              date of birth. The page is not listed or searchable.
            </p>
          </div>
          <Switch
            checked={data.enabled}
            disabled={toggle.isPending}
            onChange={(next) => toggle.mutate(next)}
            label="Share my portfolio"
          />
        </div>

        {data.enabled && data.path && (
          <>
            <div className="flex gap-2">
              <Input
                label="Your portfolio link"
                hideLabel
                readOnly
                size="sm"
                value={url}
                onFocus={(event) => event.target.select()}
                wrapperClassName="min-w-0 flex-1"
                className="text-xs"
              />
              <Button size="sm" onClick={() => void copy()}>
                <Icon name="clipboard" size={15} />
                Copy
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={url}
                target="_blank"
                rel="noreferrer noopener"
                className="border-line bg-surface text-ink hover:border-brand-300 hover:bg-brand-50/60 hover:text-brand-700 rounded-input inline-flex min-h-8 items-center gap-1.5 border px-3 py-1 text-sm font-medium shadow-xs transition"
              >
                <Icon name="external" size={15} />
                Open
              </a>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
                <Icon name="repeat" size={15} />
                Get a new link
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader title="Shareable portfolio" description="A public page you can send to anyone." />
      {body}
      <ConfirmDialog
        open={confirming}
        title="Get a new link?"
        message="The old link will stop working. Anyone you sent it to will need the new one."
        confirmLabel="Get a new link"
        tone="primary"
        isLoading={regenerate.isPending}
        onConfirm={() => regenerate.mutate()}
        onCancel={() => setConfirming(false)}
      />
    </Card>
  );
}
